import React, { useEffect, useRef, useState } from 'react';
import { Bot, Eraser, Send, ThumbsDown, ThumbsUp, X } from 'lucide-react';
import { useAssistant } from '../../contexts/AssistantContext';

function renderContent(text) {
  const parts = String(text || '').split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={index} className="font-semibold text-slate-900 dark:text-slate-50">
          {part.slice(2, -2)}
        </strong>
      );
    }
    return <span key={index}>{part}</span>;
  });
}

const SUGGESTIONS = [
  'Bugün en çok satan kumaş hangisi?',
  'Bu ay en çok alan cari kim?',
  'Bugün satış özeti'
];

const AssistantModal = () => {
  const {
    isOpen,
    messages,
    isSending,
    closeAssistant,
    clearHistory,
    sendMessage,
    submitFeedback
  } = useAssistant();
  const [draft, setDraft] = useState('');
  const [noteDraftById, setNoteDraftById] = useState({});
  const listRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    const timer = setTimeout(() => inputRef.current?.focus(), 80);
    return () => clearTimeout(timer);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || isSending) return undefined;
    const timer = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(timer);
  }, [isOpen, isSending, messages]);

  useEffect(() => {
    if (!listRef.current) return;
    listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, isSending, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (event) => {
    event.preventDefault();
    const text = draft.trim();
    if (!text || isSending) return;
    setDraft('');
    requestAnimationFrame(() => inputRef.current?.focus());
    await sendMessage(text);
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-end pointer-events-none">
      <button
        type="button"
        aria-label="Kapat"
        className="absolute inset-0 bg-slate-900/40 backdrop-blur-[1px] pointer-events-auto sm:bg-slate-900/30"
        onClick={closeAssistant}
      />

      <div
        className="
          relative pointer-events-auto flex w-full flex-col overflow-hidden
          border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-900
          h-[min(92dvh,100%)] max-h-[92dvh]
          rounded-t-2xl
          sm:mb-24 sm:mr-6 sm:h-[min(640px,78vh)] sm:w-[400px] sm:max-h-none sm:rounded-2xl
          pb-[env(safe-area-inset-bottom,0px)]
        "
      >
        <div className="flex items-center justify-between gap-3 border-b border-blue-100 bg-white px-4 py-3 dark:border-slate-700 dark:bg-slate-900">
          <div className="flex items-center gap-3 min-w-0">
            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-50 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300">
              <Bot className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold text-slate-900 dark:text-slate-50">Jansoft Asistan</div>
              <div className="truncate text-[11px] text-slate-500 dark:text-slate-400">ERP verisi + serbest sohbet · geri bildirimle öğrenir</div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={clearHistory}
              className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800"
              title="Geçmişi gizle (kayıtlar silinmez)"
            >
              <Eraser className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={closeAssistant}
              className="rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800"
              title="Kapat"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div
          ref={listRef}
          className="flex-1 space-y-3 overflow-y-auto bg-slate-50 px-4 py-4 dark:bg-slate-950"
        >
          {messages.map((message) => {
            const isUser = message.role === 'user';
            const showFeedback = !isUser && !message.isError && message.id !== 'welcome' && !String(message.id).startsWith('welcome') && message.feedback !== 'skip';
            return (
              <div
                key={message.id}
                className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}
              >
                <div className="max-w-[88%]">
                  <div
                    className={`whitespace-pre-wrap rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed shadow-sm ${
                      isUser
                        ? 'rounded-br-md bg-blue-600 text-white'
                        : message.isError
                          ? 'rounded-bl-md border border-red-200 bg-red-50 text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200'
                          : 'rounded-bl-md border border-slate-200 bg-white text-slate-800 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'
                    }`}
                  >
                    {renderContent(message.content)}
                  </div>

                  {showFeedback && (
                    <div className="mt-1.5 flex flex-col gap-1.5 pl-1">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-slate-400">Bu cevap:</span>
                        <button
                          type="button"
                          disabled={!!message.feedback}
                          onClick={() => submitFeedback(message.id, true)}
                          className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] ${
                            message.feedback === 'up'
                              ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                              : 'border-slate-200 text-slate-500 hover:border-emerald-300 hover:text-emerald-700 disabled:opacity-40'
                          }`}
                        >
                          <ThumbsUp className="h-3 w-3" />
                          Doğru
                        </button>
                        <button
                          type="button"
                          disabled={!!message.feedback}
                          onClick={() => {
                            if (message.feedback) return;
                            submitFeedback(message.id, false);
                            setNoteDraftById((prev) => ({ ...prev, [message.id]: '' }));
                          }}
                          className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] ${
                            message.feedback === 'down'
                              ? 'border-rose-300 bg-rose-50 text-rose-700'
                              : 'border-slate-200 text-slate-500 hover:border-rose-300 hover:text-rose-700 disabled:opacity-40'
                          }`}
                        >
                          <ThumbsDown className="h-3 w-3" />
                          Yanlış
                        </button>
                      </div>

                      {message.feedback === 'down' && !message.feedbackNote && noteDraftById[message.id] !== undefined && (
                        <div className="flex gap-1.5">
                          <input
                            type="text"
                            value={noteDraftById[message.id]}
                            onChange={(event) => setNoteDraftById((prev) => ({
                              ...prev,
                              [message.id]: event.target.value
                            }))}
                            placeholder="İstersen kısa not yaz, Gönder’e bas"
                            className="flex-1 rounded-lg border border-slate-200 bg-white px-2 py-1 text-[11px] outline-none focus:border-blue-400 dark:border-slate-700 dark:bg-slate-900"
                          />
                          <button
                            type="button"
                            disabled={!String(noteDraftById[message.id] || '').trim()}
                            onClick={() => {
                              const note = String(noteDraftById[message.id] || '').trim();
                              if (!note) return;
                              submitFeedback(message.id, false, note);
                              setNoteDraftById((prev) => {
                                const next = { ...prev };
                                delete next[message.id];
                                return next;
                              });
                            }}
                            className="rounded-lg bg-slate-800 px-2 py-1 text-[11px] text-white disabled:opacity-40"
                          >
                            Gönder
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {isSending && (
            <div className="flex justify-start">
              <div className="rounded-2xl rounded-bl-md border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-500 shadow-sm dark:border-slate-700 dark:bg-slate-900">
                <span className="inline-flex gap-1">
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400 [animation-delay:-0.2s]" />
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400 [animation-delay:-0.1s]" />
                  <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400" />
                </span>
              </div>
            </div>
          )}

          {messages.length <= 2 && !isSending && (
            <div className="flex flex-wrap gap-2 pt-1">
              {SUGGESTIONS.map((suggestion) => (
                <button
                  key={suggestion}
                  type="button"
                  onClick={() => sendMessage(suggestion)}
                  className="rounded-full border border-slate-200 bg-white px-3 py-1.5 text-left text-xs text-slate-600 hover:border-blue-300 hover:text-blue-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300"
                >
                  {suggestion}
                </button>
              ))}
            </div>
          )}
        </div>

        <form
          onSubmit={handleSubmit}
          className="border-t border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-900"
        >
          <div className="flex items-end gap-2 rounded-xl border border-slate-200 bg-slate-50 px-2 py-2 dark:border-slate-700 dark:bg-slate-950">
            <textarea
              ref={inputRef}
              rows={1}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  handleSubmit(event);
                }
              }}
              placeholder="Sorunu yaz…"
              className="max-h-28 min-h-[40px] flex-1 resize-none bg-transparent px-2 py-2 text-sm text-slate-800 outline-none placeholder:text-slate-400 dark:text-slate-100"
            />
            <button
              type="submit"
              disabled={isSending || !draft.trim()}
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-600 text-white transition hover:bg-blue-500 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Send className="h-4 w-4" />
            </button>
          </div>
          <p className="mt-2 px-1 text-[10px] text-slate-400">
            Geçmiş tarayıcıda saklanır. Sadece yetkili olduğun sayfaların verisini görür.
          </p>
        </form>
      </div>
    </div>
  );
};

export default AssistantModal;
